import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";

const TEACHER_PROFILE_CACHE_KEY = "teacherProfileCache";
const PROFILE_CACHE_TTL_MS = 10 * 60 * 1000;
let cachedProfile = null;
let cachedProfileScope = "";
let profileRequestPromise = null;

// localStorage is shared by tabs, so cached data is scoped to the signed-in
// teacher and tenant. Auth cookies remain HttpOnly and are never handled here.
const getCacheScope = () => {
  try {
    const email = (localStorage.getItem("authEmail") || "").trim().toLowerCase();
    const tenantId = (localStorage.getItem("activeTenantId") || "current").trim();
    return email ? `${tenantId}:${email}` : "";
  } catch {
    return "";
  }
};

const readCachedProfile = () => {
  try {
    const stored = localStorage.getItem(TEACHER_PROFILE_CACHE_KEY);
    const entry = stored ? JSON.parse(stored) : null;
    const scope = getCacheScope();
    if (!entry?.profile || !scope || entry.scope !== scope || entry.expiresAt <= Date.now()) {
      localStorage.removeItem(TEACHER_PROFILE_CACHE_KEY);
      return null;
    }
    return entry.profile;
  } catch {
    return null;
  }
};

const writeCachedProfile = (profile) => {
  const scope = getCacheScope();
  if (!scope) return;
  try {
    localStorage.setItem(TEACHER_PROFILE_CACHE_KEY, JSON.stringify({
      profile,
      scope,
      expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
    }));
    cachedProfile = profile;
    cachedProfileScope = scope;
  } catch {
    // Ignore storage write failures.
  }
};

class TeacherService {
  getCachedProfile() {
    if (cachedProfile && cachedProfileScope === getCacheScope()) return cachedProfile;

    cachedProfile = readCachedProfile();
    cachedProfileScope = cachedProfile ? getCacheScope() : "";
    return cachedProfile;
  }

  getCachedSchoolName() {
    return this.getCachedProfile()?.schoolName || "";
  }

  clearCachedProfile() {
    cachedProfile = null;
    cachedProfileScope = "";
    profileRequestPromise = null;
    try {
      localStorage.removeItem(TEACHER_PROFILE_CACHE_KEY);
    } catch {
      // Ignore unavailable storage.
    }
  }

  async getProfile(options = {}) {
    const { forceRefresh = false, preferCache = false } = options;

    if (!forceRefresh && preferCache) {
      const cached = this.getCachedProfile();
      if (cached) {
        return cached;
      }
    }

    if (!forceRefresh && profileRequestPromise) {
      return profileRequestPromise;
    }

    profileRequestPromise = api.get(API_PATHS.teacher.profile)
      .then((response) => {
        const apiResponse = response?.data || {};

        if (apiResponse.success === false) {
          throw new Error(apiResponse.message || "Unable to fetch teacher profile.");
        }

        const profile = apiResponse.data || {};
        writeCachedProfile(profile);
        return profile;
      })
      .finally(() => {
        profileRequestPromise = null;
      });

    return profileRequestPromise;
  }
}

export default new TeacherService();
